import api, { unwrap } from './client';
import type { ApiResponse } from '@/types/api';
import type {
  WhatsAppAutomationConnector,
  WhatsAppAutomationConnectorRequest,
  WhatsAppAutomationConnectorTestResult,
  WhatsAppAutomationEmergencyDisable,
  WhatsAppAutomationExecution,
  WhatsAppAutomationRule,
  WhatsAppAutomationRuleRequest,
  WhatsAppAutomationTestResult,
  WhatsAppAutomationValidateResult,
  WhatsAppChatbotFlow,
  WhatsAppChatbotFlowRequest,
  WhatsAppChatbotVersion,
  WhatsAppChatbotVersionRequest,
  WhatsAppOfficeHours,
  WhatsAppOfficeHoursRequest,
} from '@/types/whatsappAutomation';

const BASE = '/v1/social/whatsapp/automations';

export const whatsappAutomationApi = {
  listRules: () => unwrap(api.get<ApiResponse<WhatsAppAutomationRule[]>>(BASE)),

  getRule: (id: number) => unwrap(api.get<ApiResponse<WhatsAppAutomationRule>>(`${BASE}/${id}`)),

  createRule: (body: WhatsAppAutomationRuleRequest) =>
    unwrap(api.post<ApiResponse<WhatsAppAutomationRule>>(BASE, body)),

  updateRule: (id: number, body: WhatsAppAutomationRuleRequest) =>
    unwrap(api.put<ApiResponse<WhatsAppAutomationRule>>(`${BASE}/${id}`, body)),

  deleteRule: (id: number) => unwrap(api.delete<ApiResponse<void>>(`${BASE}/${id}`)),

  validateRule: (id: number) =>
    unwrap(api.post<ApiResponse<WhatsAppAutomationValidateResult>>(`${BASE}/${id}/validate`)),

  testRule: (id: number, sample?: Record<string, unknown>) =>
    unwrap(api.post<ApiResponse<WhatsAppAutomationTestResult>>(`${BASE}/${id}/test`, sample ?? {})),

  activateRule: (id: number) =>
    unwrap(api.post<ApiResponse<WhatsAppAutomationRule>>(`${BASE}/${id}/activate`)),

  pauseRule: (id: number) =>
    unwrap(api.post<ApiResponse<WhatsAppAutomationRule>>(`${BASE}/${id}/pause`)),

  listExecutions: (id: number) =>
    unwrap(api.get<ApiResponse<WhatsAppAutomationExecution[]>>(`${BASE}/${id}/executions`)),

  listOfficeHours: () =>
    unwrap(api.get<ApiResponse<WhatsAppOfficeHours[]>>(`${BASE}/office-hours`)),

  createOfficeHours: (body: WhatsAppOfficeHoursRequest) =>
    unwrap(api.post<ApiResponse<WhatsAppOfficeHours>>(`${BASE}/office-hours`, body)),

  getOfficeHours: (id: number) =>
    unwrap(api.get<ApiResponse<WhatsAppOfficeHours>>(`${BASE}/office-hours/${id}`)),

  updateOfficeHours: (id: number, body: WhatsAppOfficeHoursRequest) =>
    unwrap(api.put<ApiResponse<WhatsAppOfficeHours>>(`${BASE}/office-hours/${id}`, body)),

  deleteOfficeHours: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`${BASE}/office-hours/${id}`)),

  listChatbotFlows: () =>
    unwrap(api.get<ApiResponse<WhatsAppChatbotFlow[]>>(`${BASE}/chatbot-flows`)),

  createChatbotFlow: (body: WhatsAppChatbotFlowRequest) =>
    unwrap(api.post<ApiResponse<WhatsAppChatbotFlow>>(`${BASE}/chatbot-flows`, body)),

  getChatbotFlow: (id: number) =>
    unwrap(api.get<ApiResponse<WhatsAppChatbotFlow>>(`${BASE}/chatbot-flows/${id}`)),

  updateChatbotFlow: (id: number, body: WhatsAppChatbotFlowRequest) =>
    unwrap(api.put<ApiResponse<WhatsAppChatbotFlow>>(`${BASE}/chatbot-flows/${id}`, body)),

  deleteChatbotFlow: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`${BASE}/chatbot-flows/${id}`)),

  listChatbotVersions: (flowId: number) =>
    unwrap(api.get<ApiResponse<WhatsAppChatbotVersion[]>>(`${BASE}/chatbot-flows/${flowId}/versions`)),

  createChatbotVersion: (flowId: number, body: WhatsAppChatbotVersionRequest) =>
    unwrap(
      api.post<ApiResponse<WhatsAppChatbotVersion>>(`${BASE}/chatbot-flows/${flowId}/versions`, body),
    ),

  publishChatbotVersion: (flowId: number, versionId: number) =>
    unwrap(
      api.post<ApiResponse<WhatsAppChatbotFlow>>(
        `${BASE}/chatbot-flows/${flowId}/versions/${versionId}/publish`,
      ),
    ),

  listConnectors: () =>
    unwrap(api.get<ApiResponse<WhatsAppAutomationConnector[]>>(`${BASE}/connectors`)),

  createConnector: (body: WhatsAppAutomationConnectorRequest) =>
    unwrap(api.post<ApiResponse<WhatsAppAutomationConnector>>(`${BASE}/connectors`, body)),

  getConnector: (id: number) =>
    unwrap(api.get<ApiResponse<WhatsAppAutomationConnector>>(`${BASE}/connectors/${id}`)),

  updateConnector: (id: number, body: WhatsAppAutomationConnectorRequest) =>
    unwrap(api.put<ApiResponse<WhatsAppAutomationConnector>>(`${BASE}/connectors/${id}`, body)),

  deleteConnector: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`${BASE}/connectors/${id}`)),

  testConnector: (id: number) =>
    unwrap(api.post<ApiResponse<WhatsAppAutomationConnectorTestResult>>(`${BASE}/connectors/${id}/test`)),

  getEmergencyDisable: () =>
    unwrap(api.get<ApiResponse<WhatsAppAutomationEmergencyDisable>>(`${BASE}/emergency-disable`)),

  setEmergencyDisable: (disabled: boolean) =>
    unwrap(
      api.put<ApiResponse<WhatsAppAutomationEmergencyDisable>>(`${BASE}/emergency-disable`, {
        disabled,
      }),
    ),
};
